import { useEffect, useMemo } from "react";

import { UseQueryOptions, UseQueryResult, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useAlert } from "capitalrx-components-2.0/lib/general";
import { useAuthToken } from "capitalrx-shell";

import { getDrugListAPI, getDrugDetailsAPI, getDrugPricesListAPI, getPharmacyAPI, getUserLocationAPI } from "@/api";

interface ErrorResponse {
  message?: string;
}

type options = Omit<UseQueryOptions, "queryKey">;
export type axiosErrorType = AxiosError<ErrorResponse, unknown>;

type UseGetHook<Request, Response> = (request?: Request, options?: options) => UseQueryResult<Response, AxiosError>;

const createGetHook = <Request, Response>(
  url: string,
  apiCall: (request: Request, authToken: string) => Promise<Response>,
  defaultOptions?: options,
): UseGetHook<Request, Response> => {
  const query = (request, options) => {
    const [authToken] = useAuthToken();
    const appliedOptions = defaultOptions ? defaultOptions : {};
    const showAlert = useAlert();

    const call: UseQueryResult<Response, axiosErrorType> = useQuery<Response, axiosErrorType>({
      queryKey: [url, request],
      queryFn: async () => apiCall(request, authToken),
      ...appliedOptions,
      ...options,
    });

    const error = call.error as axiosErrorType;

    useEffect(() => {
      if (call.isError) {
        showAlert({
          message: error.response?.data?.message || error.message,
          type: "error",
          style: "toast",
        });
      }
    }, [call, showAlert]);

    return useMemo(() => call, [call, showAlert]);
  };

  return query;
};

// GET
export const useGetDrugDetails = createGetHook("/drug/get", getDrugDetailsAPI);
export const useGetPharmacy = createGetHook("/pharmacy/get", getPharmacyAPI);

// LIST
export const useGetDrugList = createGetHook("/drug/list", getDrugListAPI);
export const useGetDrugPricesList = createGetHook("/drug/prices/list", getDrugPricesListAPI);

// SEARCH
export const useGetUserLocation = createGetHook("/location/geolocate", getUserLocationAPI);
